import React from "react";
import { AlertTriangle, RefreshCw, Send, CheckCircle2, ClipboardCopy } from "lucide-react";

/**
 * Global error boundary for the admin app.
 *
 * Catches any render-time error thrown by a child route, displays a
 * styled fallback (instead of a blank page), and offers the operator
 * a one-click "Reportar" action that POSTs the error to
 * `/api/admin/error-report` so we can investigate in MongoDB audit.
 *
 * Note: React error boundaries MUST be class components — there is no
 * hooks-based equivalent for `componentDidCatch`.
 */
export default class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      info: null,
      reportState: "idle", // 'idle' | 'sending' | 'sent' | 'failed'
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Best-effort console log (helps when DevTools is open)
    // eslint-disable-next-line no-console
    console.error("[AdminErrorBoundary]", error, info);
    // Fire-and-forget: send a first auto-report so we get telemetry even
    // if the operator just refreshes without clicking "Reportar". The
    // user-driven button still works (and shows the confirmation).
    this._sendReport({ silent: true });
  }

  _sendReport = async ({ silent } = {}) => {
    if (!silent) this.setState({ reportState: "sending" });
    const { error, info } = this.state;
    try {
      const apiBase = ("" || "") + "/api/admin";
      const res = await fetch(`${apiBase}/error-report`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: error?.message || String(error || "Erro desconhecido"),
          stack: error?.stack || "",
          componentStack: info?.componentStack || "",
          path: typeof window !== "undefined" ? window.location.pathname + window.location.search : "",
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
      });
      if (!silent) this.setState({ reportState: res.ok ? "sent" : "failed" });
    } catch (_e) {
      if (!silent) this.setState({ reportState: "failed" });
    }
  };

  _copyToClipboard = async () => {
    const { error, info } = this.state;
    const payload = [
      `Message: ${error?.message || error}`,
      `Path: ${typeof window !== "undefined" ? window.location.pathname : ""}`,
      "",
      "Stack:",
      error?.stack || "(no stack)",
      "",
      "Component stack:",
      info?.componentStack || "(no component stack)",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(payload);
    } catch (_e) { /* clipboard may be blocked — silent */ }
  };

  _reload = () => {
    // Hard reload — keeps the same URL but resets all React state.
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const { error, info, reportState } = this.state;
    return (
      <div data-testid="admin-error-boundary" className="min-h-[60vh] grid place-items-center p-4">
        <div className="max-w-2xl w-full bg-white border border-red-200 rounded-lg shadow-md p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 grid place-items-center rounded-full bg-red-50 text-red-600 shrink-0">
              <AlertTriangle size={22} />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
                Algo correu mal nesta página
              </h2>
              <p className="text-sm text-[#4B5563] mt-1">
                O erro foi registado automaticamente. Pode recarregar, ou reportar
                novamente para anexar mais contexto à equipa técnica.
              </p>
            </div>
          </div>

          <details className="bg-[#FAF8F2] border border-[#E6E1D6] rounded p-3 mb-4" open>
            <summary className="cursor-pointer text-[11px] uppercase tracking-[0.15em] text-[#978D78] font-bold">
              Detalhes técnicos
            </summary>
            <div className="mt-2 text-xs space-y-2">
              <div>
                <span className="text-[#978D78] font-bold">Mensagem:</span>{" "}
                <span className="font-mono text-red-700 break-words">
                  {error?.message || String(error || "Erro desconhecido")}
                </span>
              </div>
              {error?.stack && (
                <div>
                  <span className="text-[#978D78] font-bold">Stack:</span>
                  <pre className="font-mono text-[10px] mt-1 whitespace-pre-wrap break-words text-[#4B5563] max-h-32 overflow-y-auto">
                    {error.stack}
                  </pre>
                </div>
              )}
              {info?.componentStack && (
                <div>
                  <span className="text-[#978D78] font-bold">Componente:</span>
                  <pre className="font-mono text-[10px] mt-1 whitespace-pre-wrap break-words text-[#4B5563] max-h-32 overflow-y-auto">
                    {info.componentStack}
                  </pre>
                </div>
              )}
            </div>
          </details>

          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={this._reload}
              data-testid="error-boundary-reload"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0F1829] text-white text-sm rounded hover:bg-black transition"
            >
              <RefreshCw size={14} /> Recarregar
            </button>
            <button
              onClick={() => this._sendReport({ silent: false })}
              disabled={reportState === "sending" || reportState === "sent"}
              data-testid="error-boundary-report"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded border border-[#E6E1D6] hover:bg-[#F5F2EB] disabled:opacity-50"
            >
              {reportState === "sent" ? <CheckCircle2 size={14} className="text-emerald-600" /> : <Send size={14} />}
              {reportState === "sending" && "A enviar..."}
              {reportState === "sent" && "Reportado"}
              {reportState === "failed" && "Falhou — tentar novamente"}
              {reportState === "idle" && "Reportar"}
            </button>
            <button
              onClick={this._copyToClipboard}
              data-testid="error-boundary-copy"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded border border-[#E6E1D6] hover:bg-[#F5F2EB]"
            >
              <ClipboardCopy size={14} /> Copiar detalhes
            </button>
          </div>
        </div>
      </div>
    );
  }
}
